// Hours & closures: the pause switch, the four scopes' weekly windows (7
// days × 2 sessions, each scope replaced whole), holidays, and the surcharge
// rules that ride on them. Every write reloads its section from the server.

import { useCallback, useEffect, useState } from 'react';
import { Copy, Pencil, Trash2 } from 'lucide-react';
import { api, money } from '../../lib/api';
import { useBase } from '../../scope';
import { Err, MoneyInput } from './controls';
import { Check, errMsg, Head, SaveBtn, useAction } from './shared';
import {
  byRank, type Holiday, type HoursScope, type HoursView, type HoursWindow, type ScopeHours,
  type SurchargeRule, type SurchargeRuleRequest, type SurchargeType,
} from './types';

const SCOPES: { key: HoursScope; label: string }[] = [
  { key: 'pos', label: 'POS' },
  { key: 'pickup', label: 'Pickup' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'dine_in', label: 'Dine-in' },
];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const scopeLabel = (s: HoursScope) => SCOPES.find((x) => x.key === s)?.label ?? s;

/** The server's windows as a full 7×2 editable grid (absent = closed). */
function toGrid(windows: HoursWindow[]): HoursWindow[] {
  const grid: HoursWindow[] = [];
  for (let d = 1; d <= 7; d++) {
    for (let s = 1; s <= 2; s++) {
      const w = windows.find((r) => r.dayOfWeek === d && r.sessionNo === s);
      grid.push(w ? { ...w, openTime: w.openTime?.slice(0, 5) ?? null, closeTime: w.closeTime?.slice(0, 5) ?? null }
        : { dayOfWeek: d, sessionNo: s, isOpen: false, openTime: null, closeTime: null });
    }
  }
  return grid;
}

/** The grid as the PUT body: every day's first session says open or closed;
 * a second session is sent only when it is open. */
const toWindows = (grid: HoursWindow[]): HoursWindow[] =>
  grid
    .filter((w) => w.sessionNo === 1 || w.isOpen)
    .map((w) => ({ ...w, openTime: w.isOpen ? w.openTime : null, closeTime: w.isOpen ? w.closeTime : null }));

export function Hours() {
  const base = useBase();
  const [view, setView] = useState<HoursView | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const pauseAct = useAction();

  const loadHours = useCallback(async () => {
    try {
      setView(await api.get<HoursView>(`${base}/hours`));
      setLoadErr(null);
    } catch (e) {
      setLoadErr(errMsg(e));
    }
  }, [base]);
  useEffect(() => { void loadHours(); }, [loadHours]);

  const setPaused = (pause: boolean) =>
    void pauseAct.run(async () => {
      await api.post(`${base}/${pause ? 'pause' : 'resume'}`);
      await loadHours();
    });

  return (
    <>
      <Head title="Hours & closures" />
      <Err error={loadErr} />
      {!view && !loadErr && <div className="loading">Loading…</div>}
      {view && (
        <div className="set-stack" style={{ maxWidth: 1100 }}>
          <div className="panel" data-section="pause" data-paused={view.paused}>
            <h2>Trading now</h2>
            <div className="rowline">
              <div className="grow">
                <b>{view.paused ? 'The store is paused' : 'The store is trading'}</b>
                <span className="sub">
                  Pausing closes every channel at once — online ordering shows the store as
                  temporarily closed. Hours and holidays stay as configured. Times are {view.timezone}.
                </span>
              </div>
              {view.paused ? (
                <button className="btn btn-primary btn-sm" disabled={pauseAct.busy} onClick={() => setPaused(false)}>Resume trading</button>
              ) : (
                <button className="btn btn-danger btn-sm" disabled={pauseAct.busy} onClick={() => setPaused(true)}>Pause store</button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
              {view.scopes.map((s) => (
                <span key={s.scope} className={`chip ${s.openNow ? 'chip-paid' : 'chip-unpaid'}`} data-open-now={s.openNow} data-scope={s.scope}>
                  {scopeLabel(s.scope)}: {s.openNow ? 'open now' : 'closed now'}
                </span>
              ))}
            </div>
            <Err error={pauseAct.error} />
          </div>

          <WeeklyHours base={base} scopes={view.scopes} onSaved={loadHours} />

          <div className="set-cols" style={{ maxWidth: 'none' }}>
            <Holidays base={base} onChanged={loadHours} />
            <SurchargeRules base={base} />
          </div>
        </div>
      )}
    </>
  );
}

// ── weekly windows ─────────────────────────────────────────────────

function WeeklyHours(props: { base: string; scopes: ScopeHours[]; onSaved: () => Promise<void> }) {
  const { base, scopes, onSaved } = props;
  const [tab, setTab] = useState<HoursScope>('pos');
  // Seeded once from the server; a save re-seeds only the scope it wrote, so
  // unsaved edits on the other tabs survive.
  const [grids, setGrids] = useState<Partial<Record<HoursScope, HoursWindow[]>>>(() =>
    Object.fromEntries(scopes.map((s) => [s.scope, toGrid(s.windows)])));
  const save = useAction();

  const grid = grids[tab] ?? toGrid([]);
  const setCell = (d: number, s: number, patch: Partial<HoursWindow>) =>
    setGrids((g) => ({
      ...g,
      [tab]: (g[tab] ?? toGrid([])).map((w) => (w.dayOfWeek === d && w.sessionNo === s ? { ...w, ...patch } : w)),
    }));
  const toggle = (d: number, s: number, open: boolean) => {
    const w = grid.find((x) => x.dayOfWeek === d && x.sessionNo === s)!;
    setCell(d, s, { isOpen: open, openTime: w.openTime ?? (s === 1 ? '11:00' : '17:00'), closeTime: w.closeTime ?? (s === 1 ? '15:00' : '21:00') });
  };
  const copyMonday = () =>
    setGrids((g) => {
      const cur = g[tab] ?? toGrid([]);
      return { ...g, [tab]: cur.map((w) => ({ ...cur.find((m) => m.dayOfWeek === 1 && m.sessionNo === w.sessionNo)!, dayOfWeek: w.dayOfWeek })) };
    });

  const saveScope = () =>
    void save.run(async () => {
      const saved = await api.put<ScopeHours>(`${base}/hours/${tab}`, toWindows(grid));
      setGrids((g) => ({ ...g, [saved.scope]: toGrid(saved.windows) }));
      await onSaved();
    });

  return (
    <div className="panel" data-section="hours" data-scope={tab}>
      <h2>Weekly hours</h2>
      <div className="hint" style={{ margin: '6px 0 0' }}>
        Each service keeps its own week. A session that closes before it opens runs past midnight.
      </div>
      <div className="filters" style={{ margin: '12px 0' }}>
        {SCOPES.map((s) => (
          <button key={s.key} className={'filter' + (tab === s.key ? ' on' : '')} aria-pressed={tab === s.key}
            onClick={() => setTab(s.key)}>
            {s.label}
          </button>
        ))}
      </div>
      <div className="hours-grid">
        <span className="overline">Day</span>
        <span className="overline">Session 1</span>
        <span className="overline">Session 2</span>
        {DAYS.map((label, i) => (
          <DayRow key={label} day={i + 1} label={label} grid={grid} setCell={setCell} toggle={toggle} />
        ))}
      </div>
      <Err error={save.error} />
      <div style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
        <SaveBtn busy={save.busy} saved={save.saved} onClick={saveScope} label={`Save ${scopeLabel(tab)} hours`} />
        <button className="btn btn-ghost btn-sm" onClick={copyMonday}><Copy size={14} /> Copy Monday to every day</button>
      </div>
    </div>
  );
}

function DayRow(props: {
  day: number;
  label: string;
  grid: HoursWindow[];
  setCell: (d: number, s: number, patch: Partial<HoursWindow>) => void;
  toggle: (d: number, s: number, open: boolean) => void;
}) {
  const { day, label, grid, setCell, toggle } = props;
  const session = (s: number) => {
    const w = grid.find((r) => r.dayOfWeek === day && r.sessionNo === s)!;
    const name = `${label} session ${s}`;
    return (
      <div className="hours-sess">
        <input type="checkbox" checked={w.isOpen} aria-label={`Open ${name}`} title={w.isOpen ? 'Open' : 'Closed'}
          style={{ accentColor: 'var(--primary)' }} onChange={(e) => toggle(day, s, e.target.checked)} />
        <input className="input" type="time" aria-label={`${name} opens at`} value={w.openTime ?? ''} disabled={!w.isOpen}
          onChange={(e) => setCell(day, s, { openTime: e.target.value || null })} />
        <span style={{ color: 'var(--ink-3)' }}>–</span>
        <input className="input" type="time" aria-label={`${name} closes at`} value={w.closeTime ?? ''} disabled={!w.isOpen}
          onChange={(e) => setCell(day, s, { closeTime: e.target.value || null })} />
      </div>
    );
  };
  return (
    <>
      <span className="day">{label}</span>
      {session(1)}
      {session(2)}
    </>
  );
}

// ── holidays ───────────────────────────────────────────────────────

function Holidays(props: { base: string; onChanged: () => Promise<void> }) {
  const { base, onChanged } = props;
  const [holidays, setHolidays] = useState<Holiday[] | null>(null);
  const [date, setDate] = useState('');
  const [label, setLabel] = useState('');
  const [closed, setClosed] = useState(true);
  const act = useAction();

  const load = useCallback(async () => {
    try {
      setHolidays(await api.get<Holiday[]>(`${base}/holidays`));
    } catch (e) {
      act.setError(errMsg(e));
    }
  }, [base, act.setError]);
  useEffect(() => { void load(); }, [load]);

  const add = () =>
    void act.run(async () => {
      await api.put<Holiday>(`${base}/holidays/${date}`, { label: label.trim(), isClosed: closed });
      setDate('');
      setLabel('');
      setClosed(true);
      await load();
      await onChanged();
    });
  const remove = (h: Holiday) => {
    if (!confirm(`Remove ${h.label} (${h.holidayDate})?`)) return;
    void act.run(async () => {
      await api.del(`${base}/holidays/${h.holidayDate}`);
      await load();
      await onChanged();
    });
  };

  return (
    <div className="panel" data-section="holidays">
      <h2>Holidays</h2>
      {holidays === null && <div className="loading">Loading…</div>}
      {holidays?.length === 0 && <div className="loading">No holidays set.</div>}
      {holidays?.map((h) => (
        <div key={h.holidayDate} className="rowline" data-date={h.holidayDate}>
          <span className="tm">{h.holidayDate}</span>
          <div className="grow">
            <b>{h.label}</b>
            <span className="sub">{h.isClosed ? 'Closed all day' : 'Open — holiday surcharges apply'}</span>
          </div>
          <button className="btn btn-ghost btn-sm" title={`Delete holiday ${h.holidayDate}`} aria-label={`Delete holiday ${h.holidayDate}`}
            disabled={act.busy} onClick={() => remove(h)}>
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <div className="frow" style={{ marginTop: 14 }}>
        <div className="field narrow" style={{ flexBasis: 150, minWidth: 140 }}>
          <label>Date</label>
          <input className="input" type="date" aria-label="Holiday date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="field">
          <label>Label</label>
          <input className="input" aria-label="Holiday label" placeholder="Christmas Day" value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div className="field narrow" style={{ flexBasis: 100 }}>
          <Check label="Closed" checked={closed} onChange={setClosed} />
        </div>
        <button className="btn btn-outline" disabled={act.busy || !date || !label.trim()} onClick={add}>Add holiday</button>
      </div>
      <Err error={act.error} />
    </div>
  );
}

// ── surcharge rules ────────────────────────────────────────────────

const SURCHARGE_TYPES: { key: SurchargeType; label: string }[] = [
  { key: 'public_holiday', label: 'Public holiday' },
  { key: 'credit_card', label: 'Credit card' },
  { key: 'eftpos', label: 'EFTPOS' },
  { key: 'custom', label: 'Custom' },
];

type RuleDraft = SurchargeRuleRequest & { id: string | null; mode: 'percent' | 'amount'; percentText: string };

const freshRule = (): RuleDraft => ({
  id: null, mode: 'percent', percentText: '', surchargeType: 'public_holiday', label: '',
  percentBp: null, amountInCents: null, appliesBelowInCents: null,
  appliesPos: true, appliesPickup: true, appliesDelivery: true, appliesDineIn: true, active: true,
});

const ruleValue = (r: SurchargeRule) =>
  r.percentBp !== null ? `${(r.percentBp / 100).toString()}%` : money(r.amountInCents ?? 0);

function SurchargeRules(props: { base: string }) {
  const { base } = props;
  const [rules, setRules] = useState<SurchargeRule[] | null>(null);
  const [draft, setDraft] = useState<RuleDraft | null>(null);
  const act = useAction();

  const load = useCallback(async () => {
    try {
      setRules((await api.get<SurchargeRule[]>(`${base}/surcharge-rules`)).sort(byRank));
    } catch (e) {
      act.setError(errMsg(e));
    }
  }, [base, act.setError]);
  useEffect(() => { void load(); }, [load]);

  const edit = (r: SurchargeRule) =>
    setDraft({
      ...r, id: r.surchargeId, mode: r.percentBp !== null ? 'percent' : 'amount',
      percentText: r.percentBp !== null ? (r.percentBp / 100).toString() : '',
    });

  const body = (d: RuleDraft): SurchargeRuleRequest => {
    const pct = Number(d.percentText);
    return {
      surchargeType: d.surchargeType,
      label: d.label.trim(),
      percentBp: d.mode === 'percent' && d.percentText.trim() !== '' && Number.isFinite(pct) ? Math.round(pct * 100) : null,
      amountInCents: d.mode === 'amount' ? d.amountInCents : null,
      appliesBelowInCents: d.appliesBelowInCents,
      appliesPos: d.appliesPos,
      appliesPickup: d.appliesPickup,
      appliesDelivery: d.appliesDelivery,
      appliesDineIn: d.appliesDineIn,
      active: d.active,
    };
  };

  const saveRule = () => {
    if (!draft) return;
    void act.run(async () => {
      if (draft.id) await api.put<SurchargeRule>(`${base}/surcharge-rules/${draft.id}`, body(draft));
      else await api.post<SurchargeRule>(`${base}/surcharge-rules`, body(draft));
      setDraft(null);
      await load();
    });
  };
  const remove = (r: SurchargeRule) => {
    if (!confirm(`Delete the surcharge “${r.label}”?`)) return;
    void act.run(async () => {
      await api.del(`${base}/surcharge-rules/${r.surchargeId}`);
      setDraft((d) => (d?.id === r.surchargeId ? null : d));
      await load();
    });
  };

  const channels = (r: SurchargeRuleRequest) =>
    [r.appliesPos && 'POS', r.appliesPickup && 'pickup', r.appliesDelivery && 'delivery', r.appliesDineIn && 'dine-in'].filter(Boolean).join(', ');

  return (
    <div className="panel" data-section="surcharges">
      <h2>Surcharge rules</h2>
      {rules === null && <div className="loading">Loading…</div>}
      {rules?.length === 0 && <div className="loading">No surcharges.</div>}
      {rules?.map((r) => (
        <div key={r.surchargeId} className="rowline" data-surcharge-id={r.surchargeId}>
          <div className="grow">
            <b>{r.label}{r.active ? '' : ' (off)'}</b>
            <span className="sub">
              {SURCHARGE_TYPES.find((t) => t.key === r.surchargeType)?.label} · {ruleValue(r)}
              {r.appliesBelowInCents !== null ? ` · below ${money(r.appliesBelowInCents)}` : ''} · {channels(r) || 'no channels'}
            </span>
          </div>
          <button className="btn btn-ghost btn-sm" title={`Edit ${r.label}`} aria-label={`Edit ${r.label}`} onClick={() => edit(r)}>
            <Pencil size={14} />
          </button>
          <button className="btn btn-ghost btn-sm" title={`Delete ${r.label}`} aria-label={`Delete ${r.label}`} disabled={act.busy} onClick={() => remove(r)}>
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      {!draft && (
        <div style={{ marginTop: 12 }}>
          <button className="btn btn-outline" onClick={() => setDraft(freshRule())}>New surcharge</button>
        </div>
      )}
      {draft && (
        <div className="set-stack" style={{ marginTop: 14, gap: 12 }}>
          <div className="overline">{draft.id ? 'Edit surcharge' : 'New surcharge'}</div>
          <div className="frow">
            <div className="field">
              <label>Type</label>
              <select className="input" aria-label="Surcharge type" value={draft.surchargeType}
                onChange={(e) => setDraft({ ...draft, surchargeType: e.target.value as SurchargeType })}>
                {SURCHARGE_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Label</label>
              <input className="input" aria-label="Surcharge label" placeholder="Public holiday 15%" value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
            </div>
          </div>
          <div className="frow">
            <div className="field narrow" style={{ flexBasis: 140 }}>
              <label>Charged as</label>
              <select className="input" aria-label="Charged as" value={draft.mode}
                onChange={(e) => setDraft({ ...draft, mode: e.target.value as RuleDraft['mode'] })}>
                <option value="percent">a rate</option>
                <option value="amount">an amount</option>
              </select>
            </div>
            {draft.mode === 'percent' ? (
              <div className="field narrow">
                <label>Rate (%)</label>
                <input className="input money" inputMode="decimal" aria-label="Rate (%)" placeholder="15" value={draft.percentText}
                  onChange={(e) => setDraft({ ...draft, percentText: e.target.value })} />
              </div>
            ) : (
              <div className="field narrow">
                <label>Amount ($)</label>
                <MoneyInput title="Surcharge amount" value={draft.amountInCents} onChange={(c) => setDraft({ ...draft, amountInCents: c })} />
              </div>
            )}
            <div className="field narrow" style={{ flexBasis: 150 }}>
              <label>Only below ($, optional)</label>
              <MoneyInput title="Applies below" placeholder="" value={draft.appliesBelowInCents}
                onChange={(c) => setDraft({ ...draft, appliesBelowInCents: c })} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <Check label="POS" checked={draft.appliesPos} onChange={(v) => setDraft({ ...draft, appliesPos: v })} />
            <Check label="Pickup" checked={draft.appliesPickup} onChange={(v) => setDraft({ ...draft, appliesPickup: v })} />
            <Check label="Delivery" checked={draft.appliesDelivery} onChange={(v) => setDraft({ ...draft, appliesDelivery: v })} />
            <Check label="Dine-in" checked={draft.appliesDineIn} onChange={(v) => setDraft({ ...draft, appliesDineIn: v })} />
            <Check label="Active" checked={draft.active} onChange={(v) => setDraft({ ...draft, active: v })} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <SaveBtn busy={act.busy} saved={false} onClick={saveRule} disabled={!draft.label.trim()}
              label={draft.id ? 'Save surcharge' : 'Create surcharge'} />
            <button className="btn btn-ghost" onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </div>
      )}
      <Err error={act.error} />
    </div>
  );
}
